import {
  Snackbar,
  Alert,
  CircularProgress,
  Backdrop,
  Icon,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  MenuItem,
  Button,
  Switch,
  FormControlLabel,
  Divider,
  InputAdornment,
  Grid,
  Chip,
} from "@mui/material";

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDInput from "theme/components/MDInput";
import MDButton from "theme/components/MDButton";

import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

import axios from "axios.js";
import usePageTitle from "app/pageTitle";

import DataTable from "theme/Tables/DataTable";
import { useAwaitDialog } from "app/components/status_dialog";

export default function OpsSalesRulePage() {
  const { ref } = useParams();
  const title = "Sales Rule Details";
  usePageTitle(title);

  const navigate = useNavigate();
  const statusDialog = useAwaitDialog();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("info");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [refreshPage, setRefreshPage] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);

  // Rule fields
  const [resultData, setResultData] = useState(null);
  const [ruleData, setRuleData] = useState(null);
  const [ruleTitle, setRuleTitle] = useState("");
  const [priceModel, setPriceModel] = useState("FIXED");
  const [enableDiscount, setEnableDiscount] = useState(false);
  const [discount, setDiscount] = useState(0);
  const [isDiscountPercentage, setIsDiscountPercentage] = useState(false);
  const [paymentEnabled, setPaymentEnabled] = useState(true);
  const [inventoryTracked, setInventoryTracked] = useState(false);
  const [presenceRequired, setPresenceRequired] = useState(false);
  const [status, setStatus] = useState(true);

  // Dialog states
  const [openRuleDialog, setOpenRuleDialog] = useState(false);
  const [openRelDialog, setOpenRelDialog] = useState(false);
  const [openProdDialog, setOpenProdDialog] = useState(false);
  const [openEditProdDialog, setOpenEditProdDialog] = useState(false);

  // Selector & input states
  const [selectedRelRef, setSelectedRelRef] = useState("");
  const [selectedProdRef, setSelectedProdRef] = useState("");
  const [productUnitPrice, setProductUnitPrice] = useState("");

  // Edit linked product item state
  const [editingProdRelRef, setEditingProdRelRef] = useState("");
  const [editProductUnitPrice, setEditProductUnitPrice] = useState("");

  // Options from API response
  const [availableRelationships, setAvailableRelationships] = useState([]);
  const [availableProducts, setAvailableProducts] = useState([]);

  // Linked items from API response
  const [relationshipsList, setRelationshipsList] = useState([]);
  const [productsList, setProductsList] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      try {
        const result = await axios.get(`/portal/ops/sales/rules/${ref}/fetch.json`);
        setShowLoading(false);
        if (result.data.status === "success") {
          setResultData(result.data);
          const data = result.data.data;
          setRuleData(data);
          setRuleTitle(data.title || "");
          setPriceModel(data.price_model || "FIXED");
          const hasDiscount = Boolean(data.discount && data.discount > 0);
          setEnableDiscount(hasDiscount);
          setDiscount(data.discount || 0);
          setIsDiscountPercentage(data.is_discount_percentage || false);
          setPaymentEnabled(data.payment_enabled !== undefined ? data.payment_enabled : true);
          setInventoryTracked(
            data.inventory_tracked !== undefined ? data.inventory_tracked : false
          );
          setPresenceRequired(
            data.presence_required !== undefined ? data.presence_required : false
          );
          setStatus(data.status !== undefined ? data.status : true);

          // Populate selects and lists matching Django keys
          setAvailableRelationships(data.relationships || []);
          setRelationshipsList(data.rule_relationships || []);
          setAvailableProducts(data.products || []);
          setProductsList(data.rule_products || []);

          setPageLoaded(true);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data ? result.data.data.message : "Error loading details.");
          setOpen(true);
        }
      } catch (err) {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg("Unable to load sales rule details.");
        setOpen(true);
      }
    };

    fetchData();
  }, [ref, refreshPage]);

  async function saveSalesRule() {
    setShowLoading(true);
    const json = {
      reference: ref,
      title: ruleTitle,
      price_model: priceModel,
      discount: enableDiscount ? parseFloat(discount) : 0,
      is_discount_percentage: enableDiscount ? isDiscountPercentage : false,
      payment_enabled: paymentEnabled,
      inventory_tracked: inventoryTracked,
      presence_required: presenceRequired,
      status: status,
    };

    try {
      const result = await axios.post("/portal/ops/sales/rules/edit.json", json);
      setShowLoading(false);
      if (result.data.status === "success") {
        setOpenRuleDialog(false);
        await statusDialog.show(result.data.data.message, "success", "Success!");
        setRefreshPage((prev) => !prev);
      } else {
        setSeverity("error");
        setErrMsg(result.data.data ? result.data.data.message : "Failed to update.");
        setOpen(true);
      }
    } catch (err) {
      setShowLoading(false);
      setSeverity("error");
      setErrMsg("Failed to update sales rule.");
      setOpen(true);
    }
  }

  async function handleAddRelationship() {
    if (!selectedRelRef) return;
    setOpenRelDialog(false);
    setShowLoading(true);
    try {
      const result = await axios.post("/portal/ops/sales/rules/relationships/add.json", {
        reference: ref,
        relationship: selectedRelRef,
      });
      setShowLoading(false);
      setSelectedRelRef("");
      if (result.data.status === "success") {
        await statusDialog.show(result.data.data.message, "success", "Success!");
        setRefreshPage((prev) => !prev);
      } else {
        setSeverity("error");
        setErrMsg(result.data.data ? result.data.data.message : "Failed to add relationship.");
        setOpen(true);
      }
    } catch (err) {
      setShowLoading(false);
      setSeverity("error");
      setErrMsg("Failed to link relationship.");
      setOpen(true);
    }
  }

  async function handleRemoveRelationship(relRef) {
    setShowLoading(true);
    try {
      const result = await axios.get(`/portal/ops/sales/rules/relationships/${relRef}/delete.json`);
      setShowLoading(false);
      if (result.data.status === "success") {
        await statusDialog.show(result.data.data.message, "success", "Success!");
        setRefreshPage((prev) => !prev);
      } else {
        setSeverity("error");
        setErrMsg(result.data.data ? result.data.data.message : "Failed to remove relationship.");
        setOpen(true);
      }
    } catch (err) {
      setShowLoading(false);
      setSeverity("error");
      setErrMsg("Failed to remove relationship.");
      setOpen(true);
    }
  }

  async function handleAddProduct() {
    if (!selectedProdRef) return;
    setOpenProdDialog(false);
    setShowLoading(true);
    try {
      const result = await axios.post("/portal/ops/sales/rules/products/add.json", {
        reference: ref,
        product: selectedProdRef,
        unit_price: productUnitPrice ? parseFloat(productUnitPrice) : null,
      });
      setShowLoading(false);
      setSelectedProdRef("");
      setProductUnitPrice("");
      if (result.data.status === "success") {
        await statusDialog.show(result.data.data.message, "success", "Success!");
        setRefreshPage((prev) => !prev);
      } else {
        setSeverity("error");
        setErrMsg(result.data.data ? result.data.data.message : "Failed to add product.");
        setOpen(true);
      }
    } catch (err) {
      setShowLoading(false);
      setSeverity("error");
      setErrMsg("Failed to link product.");
      setOpen(true);
    }
  }

  function openEditProductModal(item) {
    setEditingProdRelRef(item.reference);
    setEditProductUnitPrice(
      item.unit_price !== null && item.unit_price !== undefined ? item.unit_price : ""
    );
    setOpenEditProdDialog(true);
  }

  async function handleEditProduct() {
    setOpenEditProdDialog(false);
    setShowLoading(true);
    try {
      const result = await axios.post("/portal/ops/sales/rules/products/edit.json", {
        reference: editingProdRelRef,
        unit_price: editProductUnitPrice ? parseFloat(editProductUnitPrice) : null,
      });
      console.log(result);
      setShowLoading(false);

      if (result.data.status === "success") {
        await statusDialog.show(result.data.data.message, "success", "Success!");
        setRefreshPage((prev) => !prev);
      } else {
        setSeverity("error");
        setErrMsg(result.data.data ? result.data.data.message : "Failed to edit product.");
        setOpen(true);
      }
    } catch (err) {
      setShowLoading(false);
      setSeverity("error");
      setErrMsg("Failed to update product pricing.");
      setOpen(true);
    }
  }

  async function handleRemoveProduct(prodRef) {
    setShowLoading(true);
    try {
      const result = await axios.get(`/portal/ops/sales/rules/products/${prodRef}/delete.json`);
      setShowLoading(false);
      if (result.data.status === "success") {
        await statusDialog.show(result.data.data.message, "success", "Success!");
        setRefreshPage((prev) => !prev);
      } else {
        setSeverity("error");
        setErrMsg(result.data.data ? result.data.data.message : "Failed to remove product.");
        setOpen(true);
      }
    } catch (err) {
      setShowLoading(false);
      setSeverity("error");
      setErrMsg("Failed to remove product.");
      setOpen(true);
    }
  }

  const relationshipColumns = [
    { Header: "Name", accessor: "title", align: "left" },
    { Header: "Actions", accessor: "actions", align: "right" },
  ];

  const relationshipRows = relationshipsList.map((item) => ({
    title: item.title,
    actions: (
      <MDBox display="flex" gap={1}>
        <Tooltip title="View Sales">
          <MDButton
            size="small"
            variant="outlined"
            color="success"
            iconOnly
            onClick={() => navigate(`/ops/sales/relationship/${item.relationship_reference}`)}
          >
            <Icon fontSize="small">list</Icon>
          </MDButton>
        </Tooltip>
        <Tooltip title="Remove Business Entity">
          <MDButton
            size="small"
            variant="outlined"
            color="error"
            iconOnly
            onClick={() => handleRemoveRelationship(item.reference)}
          >
            <Icon fontSize="small">delete</Icon>
          </MDButton>
        </Tooltip>
      </MDBox>
    ),
  }));

  const productColumns = [
    { Header: "Product", accessor: "title", align: "left" },
    { Header: "Unit Price", accessor: "unit_price", align: "center" },
    { Header: "Actions", accessor: "actions", align: "right" },
  ];

  const productRows = productsList.map((item) => ({
    title: item.title,
    unit_price:
      item.unit_price !== null && item.unit_price !== undefined
        ? `${resultData.currency}${(item.unit_price / 100).toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`
        : "Default",
    actions: (
      <MDBox display="flex" justifyContent="flex-end" gap={1}>
        <Tooltip title="Edit Custom Price">
          <MDButton
            size="small"
            variant="outlined"
            color="info"
            iconOnly
            onClick={() => openEditProductModal(item)}
          >
            <Icon fontSize="small">edit</Icon>
          </MDButton>
        </Tooltip>
        <Tooltip title="Remove Product">
          <MDButton
            size="small"
            variant="outlined"
            color="error"
            iconOnly
            onClick={() => handleRemoveProduct(item.reference)}
          >
            <Icon fontSize="small">delete</Icon>
          </MDButton>
        </Tooltip>
      </MDBox>
    ),
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>

      {/* Edit Rule Dialog */}
      <Dialog open={openRuleDialog} fullWidth maxWidth="xs">
        <DialogTitle>Edit Sales Rule</DialogTitle>
        <DialogContent>
          <MDBox mt={1}>
            <MDTypography
              variant="caption"
              color="text"
              fontWeight="bold"
              textTransform="uppercase"
            >
              General Details
            </MDTypography>
            <MDBox mt={2}>
              <MDInput
                label="Title"
                fullWidth
                value={ruleTitle}
                onChange={(e) => setRuleTitle(e.target.value)}
              />
            </MDBox>
            <MDBox mt={3}>
              <MDInput
                select
                label="Price Model"
                value={priceModel}
                onChange={(e) => setPriceModel(e.target.value)}
                fullWidth
                SelectProps={{ style: { padding: "12px 0px" } }}
              >
                <MenuItem value="FIXED">Fixed Pricing</MenuItem>
                <MenuItem value="OPEN">Variable Pricing</MenuItem>
              </MDInput>
            </MDBox>
            <MDBox mt={3}>
              <MDTypography
                variant="caption"
                color="text"
                fontWeight="bold"
                textTransform="uppercase"
              >
                Discount Configuration
              </MDTypography>
              <MDBox mt={1}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={enableDiscount}
                      onChange={(e) => {
                        setEnableDiscount(e.target.checked);
                        if (!e.target.checked) setDiscount(0);
                      }}
                      color="dark"
                    />
                  }
                  label="Enable Discount"
                />
              </MDBox>
              {enableDiscount && (
                <>
                  <MDBox mt={1}>
                    <MDInput
                      label="Discount Value"
                      type="number"
                      fullWidth
                      value={discount}
                      onChange={(e) => setDiscount(e.target.value)}
                      InputProps={{
                        startAdornment: !isDiscountPercentage ? (
                          <InputAdornment position="start">
                            <MDTypography variant="body2">{resultData.currency}</MDTypography>
                          </InputAdornment>
                        ) : null,
                        endAdornment: isDiscountPercentage ? (
                          <InputAdornment position="end">%</InputAdornment>
                        ) : null,
                      }}
                    />
                  </MDBox>
                  <MDBox mt={1}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isDiscountPercentage}
                          onChange={(e) => setIsDiscountPercentage(e.target.checked)}
                          color="dark"
                        />
                      }
                      label="Is Discount Percentage?"
                    />
                  </MDBox>
                </>
              )}
            </MDBox>
            <MDBox mt={3}>
              <MDTypography
                variant="caption"
                color="text"
                fontWeight="bold"
                textTransform="uppercase"
              >
                Policy & Controls
              </MDTypography>
              <MDBox mt={1} display="flex" flexDirection="column">
                <FormControlLabel
                  control={
                    <Switch
                      checked={paymentEnabled}
                      onChange={(e) => setPaymentEnabled(e.target.checked)}
                      color="dark"
                    />
                  }
                  label="Payment Enabled"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={inventoryTracked}
                      onChange={(e) => setInventoryTracked(e.target.checked)}
                      color="dark"
                    />
                  }
                  label="Inventory Tracking"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={presenceRequired}
                      onChange={(e) => setPresenceRequired(e.target.checked)}
                      color="dark"
                    />
                  }
                  label="Physical Presence Required"
                />
              </MDBox>
            </MDBox>
            <MDBox mt={3}>
              <MDTypography
                variant="caption"
                color="text"
                fontWeight="bold"
                textTransform="uppercase"
              >
                Rule Status
              </MDTypography>
              <MDBox mt={1} display="flex" flexDirection="column">
                <FormControlLabel
                  control={
                    <Switch
                      checked={status}
                      onChange={(e) => setStatus(e.target.checked)}
                      color="dark"
                    />
                  }
                  label={status ? "Enabled" : "Disabled"}
                />
              </MDBox>
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setOpenRuleDialog(false)} color="dark" variant="outlined">
            Cancel
          </MDButton>
          <MDButton onClick={saveSalesRule} color="dark" variant="contained">
            Update
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Add Relationship Dialog */}
      <Dialog open={openRelDialog} fullWidth maxWidth="xs">
        <DialogTitle>Link Business Entity</DialogTitle>
        <DialogContent>
          <MDBox mt={1}>
            <MDInput
              select
              label="Select Business Entity"
              value={selectedRelRef}
              onChange={(e) => setSelectedRelRef(e.target.value)}
              fullWidth
              SelectProps={{ style: { padding: "12px 0px" } }}
            >
              {availableRelationships.map((rel) => (
                <MenuItem key={rel.reference} value={rel.reference}>
                  {rel.title}
                </MenuItem>
              ))}
            </MDInput>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setOpenRelDialog(false)} color="dark" variant="outlined">
            Cancel
          </MDButton>
          <MDButton onClick={handleAddRelationship} color="dark" variant="contained">
            Add
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Add Product Dialog */}
      <Dialog open={openProdDialog} fullWidth maxWidth="xs">
        <DialogTitle>Link Product</DialogTitle>
        <DialogContent>
          <MDBox mt={1}>
            <MDInput
              select
              label="Select Product"
              value={selectedProdRef}
              onChange={(e) => setSelectedProdRef(e.target.value)}
              fullWidth
              SelectProps={{ style: { padding: "12px 0px" } }}
            >
              {availableProducts.map((prod) => (
                <MenuItem key={prod.reference} value={prod.reference}>
                  {prod.title}
                </MenuItem>
              ))}
            </MDInput>
          </MDBox>
          <MDBox mt={3}>
            <MDInput
              label="Override Unit Price (Optional)"
              type="number"
              fullWidth
              value={productUnitPrice}
              onChange={(e) => setProductUnitPrice(e.target.value)}
            />
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setOpenProdDialog(false)} color="dark" variant="outlined">
            Cancel
          </MDButton>
          <MDButton onClick={handleAddProduct} color="dark" variant="contained">
            Add
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Edit Product Dialog */}
      <Dialog open={openEditProdDialog} fullWidth maxWidth="xs">
        <DialogTitle>Edit Product Unit Price</DialogTitle>
        <DialogContent>
          <MDBox mt={1}>
            <MDInput
              label="Custom Unit Price"
              type="number"
              fullWidth
              value={editProductUnitPrice}
              onChange={(e) => setEditProductUnitPrice(e.target.value)}
            />
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setOpenEditProdDialog(false)} color="dark" variant="outlined">
            Cancel
          </MDButton>
          <MDButton onClick={handleEditProduct} color="dark" variant="contained">
            Save
          </MDButton>
        </DialogActions>
      </Dialog>

      {pageLoaded && ruleData && (
        <MDBox mb={5}>
          <MDBox mb={2} display="flex" justifyContent="space-between">
            <Button
              color="dark"
              size="small"
              startIcon={<Icon>west</Icon>}
              onClick={() => navigate("/ops/sales-rules")}
            >
              Back to Sales Rules
            </Button>
            <Button
              color="dark"
              size="small"
              startIcon={<Icon>list</Icon>}
              onClick={() => navigate(`/ops/sales/rule/${ref}`)}
            >
              Sales Orders
            </Button>
          </MDBox>

          {/* Expanded Rule Attributes Card */}
          <Card sx={{ p: 3, mb: 3 }}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <MDBox display="flex" alignItems="center" gap={1.5}>
                <MDTypography variant="h4" fontWeight="bold">
                  {ruleData.title}
                </MDTypography>
                <Chip
                  label={ruleData.status ? "Active" : "Inactive"}
                  color={ruleData.status ? "success" : "default"}
                  size="small"
                />
              </MDBox>
              <MDButton
                color="dark"
                size="small"
                variant="contained"
                startIcon={<Icon>edit</Icon>}
                onClick={() => setOpenRuleDialog(true)}
              >
                Edit Rule
              </MDButton>
            </MDBox>

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={4}>
                <MDTypography
                  variant="caption"
                  color="text"
                  fontWeight="bold"
                  textTransform="uppercase"
                >
                  Price Model
                </MDTypography>
                <MDTypography variant="body2" fontWeight="medium">
                  {ruleData.price_model}
                </MDTypography>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <MDTypography
                  variant="caption"
                  color="text"
                  fontWeight="bold"
                  textTransform="uppercase"
                >
                  Discount
                </MDTypography>
                <MDTypography variant="body2" fontWeight="medium">
                  {ruleData.discount && ruleData.discount > 0
                    ? ruleData.is_discount_percentage
                      ? `${ruleData.discount}%`
                      : `${resultData.currency}${ruleData.discount.toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}`
                    : "None"}
                </MDTypography>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <MDTypography
                  variant="caption"
                  color="text"
                  fontWeight="bold"
                  textTransform="uppercase"
                >
                  Payment Enabled
                </MDTypography>
                <MDTypography variant="body2" fontWeight="medium">
                  {ruleData.payment_enabled ? "Yes" : "No"}
                </MDTypography>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <MDTypography
                  variant="caption"
                  color="text"
                  fontWeight="bold"
                  textTransform="uppercase"
                >
                  Inventory Tracking
                </MDTypography>
                <MDTypography variant="body2" fontWeight="medium">
                  {ruleData.inventory_tracked ? "Yes" : "No"}
                </MDTypography>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <MDTypography
                  variant="caption"
                  color="text"
                  fontWeight="bold"
                  textTransform="uppercase"
                >
                  Physical Presence Required
                </MDTypography>
                <MDTypography variant="body2" fontWeight="medium">
                  {ruleData.presence_required ? "Yes" : "No"}
                </MDTypography>
              </Grid>
            </Grid>
          </Card>

          <Grid container spacing={3}>
            <Grid item xs={12} md={12} lg={12}>
              <Card>
                <MDBox p={2} display="flex" justifyContent="space-between" alignItems="center">
                  <MDBox>
                    <MDTypography variant="h6">Business Entities</MDTypography>
                    <MDTypography variant="body2" fontSize={14}>
                      Set which business entities to apply this rule to.
                    </MDTypography>
                  </MDBox>
                  <MDButton
                    color="dark"
                    size="small"
                    iconOnly
                    onClick={() => setOpenRelDialog(true)}
                  >
                    <Icon>add</Icon>
                  </MDButton>
                </MDBox>
                <DataTable
                  table={{ columns: relationshipColumns, rows: relationshipRows }}
                  showTotalEntries={false}
                  entriesPerPage={false}
                  canSearch={false}
                />
              </Card>
            </Grid>

            <Grid item xs={12} md={12} lg={12}>
              <Card>
                <MDBox p={2} display="flex" justifyContent="space-between" alignItems="center">
                  <MDBox>
                    <MDTypography variant="h6">Products</MDTypography>
                    <MDTypography variant="body2" fontSize={14}>
                      Restrict sales to only the specified products or leave empty to allow all
                      products.
                    </MDTypography>
                  </MDBox>
                  <MDButton
                    color="dark"
                    size="small"
                    iconOnly
                    onClick={() => setOpenProdDialog(true)}
                  >
                    <Icon>add</Icon>
                  </MDButton>
                </MDBox>
                <DataTable
                  table={{ columns: productColumns, rows: productRows }}
                  showTotalEntries={false}
                  entriesPerPage={false}
                  canSearch={false}
                />
              </Card>
            </Grid>
          </Grid>
        </MDBox>
      )}

      <Footer />
    </DashboardLayout>
  );
}
